/**
 * Frontend helpers for external-apply (Delco/EFC) vs embedded-form (Scoober) recruitment tracking.
 *
 * - **`window.useExternalApplyTracking`** (from `Language::usesExternalApplyTracking()` /
 *   `GlobalDataProvider::useExternalApplyTracking()`). True when the language is Delco or EFC with
 *   an external apply URL. Use for GTM Pageview location fields, hybrid `apply_submit` types, and
 *   `getCityValueForPageTracking` country labels.
 * - **`window.language.isDelcoPage`** / **`window.language.is_efc`** (Nova business-model flags).
 *   Use for GTM `pageGroup` display names on recruitment landing pages.
 * - **`usesEfcTracking()`** gates the EFC measurement-plan shape.
 */

// GTM `pageGroup` for EFC recruitment landing pages, fixed by the client's measurement plan.
export const EFC_RECRUITMENT_PAGE_GROUP = 'EFC Courier Recruitment Landing Page';

/**
 * Gate for the EFC measurement-plan tracking shape (pageGroup, `ut.env` mapping, `apply_submit`).
 * Enabled per country via the COUNTRIES_WITH_EFC_TRACKING env list (from `Language::usesEfcTracking()`
 * / `GlobalDataProvider::useEfcTracking()`). EFC languages of countries not in the list keep the
 * plain EFC baseline: `EFC` pageGroup, raw `qa`/`prod` env values, and no `apply_submit`.
 */
export function usesEfcTracking() {
    return window.useEfcTracking === true;
}

// GTM `apply_submit` component_type for flows that can be external (Delco) or embedded (Scoober)
export function applySubmitHybridButtonType() {
    return window.hasExternalApplyForm ? 'button delco' : 'button scoober';
}

// GTM `apply_submit` component_type across all recruitment models (EFC value fixed by the measurement plan)
export function applySubmitComponentType() {
    return usesEfcTracking() ? 'button' : applySubmitHybridButtonType();
}

// Whether tapping an Apply Now CTA must emit `apply_submit` (external-apply markets and measurement-plan EFC)
export function shouldTrackApplySubmit() {
    return window.useExternalApplyTracking || usesEfcTracking();
}

// City string used in map / slider style events (not the Pageview `city` vs `application_city` split)
export function getCityValueForPageTracking() {
    const page = typeof window !== 'undefined' ? window.page : undefined;
    const currentCity = typeof window !== 'undefined' ? window.currentCity : undefined;
    const trackingName = currentCity?.tracking_name;

    if (trackingName) {
        if (page === 'home_city') {
            return trackingName;
        }
        return window.useExternalApplyTracking ? 'Country' : `${trackingName} - Country`;
    }

    return 'Country';
}

// Mutates `pageData` with either `city` (Scoober) or `application_city` (Delco/EFC city page only).
// Courier hub / Inside Track pages skip location fields here.
export function appendRecruitmentLocationToPageData(pageData, { isInsideTrack, isCityPage, getCityValue }) {
    if (isInsideTrack()) {
        return;
    }

    if (window.useExternalApplyTracking || usesEfcTracking()) {
        if (isCityPage()) {
            pageData.application_city = getCityValue();
        }
    } else {
        pageData.city = getCityValue();
    }
}

/**
 * GTM recruitment home `pageGroup` display name (must stay in sync with e2e / analytics contracts).
 * EFC markets without measurement-plan tracking keep the plain `EFC` label.
 * @param {string | undefined} pageGroup
 * @returns {string | undefined}
 */
export function resolveRecruitmentPageGroupForGtm(pageGroup) {
    if (pageGroup !== 'home') {
        return pageGroup;
    }
    if (window.language?.is_efc) {
        return usesEfcTracking() ? EFC_RECRUITMENT_PAGE_GROUP : 'EFC';
    }
    if (window.language?.isDelcoPage) {
        return 'Delco Courier Recruitment Landing Page';
    }
    return pageGroup;
}
